/**
 * Hook para gestionar el mantenimiento de plotters
 * Proporciona funciones CRUD y queries con React Query
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/apiClient';
import { useToast } from '@/hooks/use-toast';
import type {
  PlotterMaintenance,
  PlotterMaintenanceWithMachine,
  PlotterMaintenanceStatus
} from '@/types/plotterMaintenance';
import type {
  PlotterMaintenanceFormData,
  CompleteMaintenanceFormData
} from '@/schemas/plotterMaintenanceSchemas';

const QUERY_KEY = 'plotter-maintenance';

interface UseMaintenanceListOptions {
  machineId?: string | null;
  status?: PlotterMaintenanceStatus | 'all';
  dateFrom?: string;
  dateTo?: string;
}

/**
 * Hook para listar tareas de mantenimiento
 */
export function usePlotterMaintenanceList(options: UseMaintenanceListOptions = {}) {
  const { machineId, status, dateFrom, dateTo } = options;

  return useQuery({
    queryKey: [QUERY_KEY, 'list', machineId, status, dateFrom, dateTo],
    queryFn: async () => {
      let query = api
        .from('plotter_maintenance')
        .select('*')
        .eq('is_active', true)
        .order('scheduled_date', { ascending: true });

      if (machineId) {
        query = query.eq('machine_id', machineId);
      }

      if (status && status !== 'all') {
        query = query.eq('status', status);
      }

      if (dateFrom) {
        query = query.gte('scheduled_date', dateFrom);
      }

      if (dateTo) {
        query = query.lte('scheduled_date', dateTo);
      }

      const { data, error } = await query;

      if (error) throw error;
      return (data || []) as PlotterMaintenance[];
    },
  });
}

/**
 * Hook para obtener una tarea de mantenimiento por ID
 */
export function usePlotterMaintenanceById(id: string | null) {
  return useQuery({
    queryKey: [QUERY_KEY, 'detail', id],
    queryFn: async () => {
      if (!id) return null;

      const { data, error } = await api
        .from('plotter_maintenance')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      return data as PlotterMaintenance;
    },
    enabled: !!id,
  });
}

/**
 * Hook para obtener plotters (máquinas tipo plotter)
 */
export function usePlotters() {
  return useQuery({
    queryKey: ['machines', 'plotters'],
    queryFn: async () => {
      const { data, error } = await api
        .from('machines')
        .select('id, name, code, model, type, status')
        .eq('is_active', true)
        .order('name', { ascending: true });

      if (error) throw error;
      return data || [];
    },
  });
}

/**
 * Hook para obtener operadores/técnicos
 */
export function useOperators() {
  return useQuery({
    queryKey: ['profiles', 'operators'],
    queryFn: async () => {
      const { data, error } = await api
        .from('profiles')
        .select('id, full_name')
        .eq('is_active', true)
        .order('full_name', { ascending: true });

      if (error) throw error;
      return data || [];
    },
  });
}

/**
 * Hook para crear una tarea de mantenimiento
 */
export function useCreateMaintenance() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: PlotterMaintenanceFormData & { created_by?: string }) => {
      const { error, data: result } = await api
        .from('plotter_maintenance')
        .insert({
          machine_id: data.machine_id,
          task_type: data.task_type,
          title: data.title,
          description: data.description || null,
          scheduled_date: data.scheduled_date,
          estimated_duration_minutes: data.estimated_duration_minutes,
          is_recurring: data.is_recurring,
          recurrence_pattern: data.recurrence_pattern || null,
          recurrence_interval: data.recurrence_interval,
          assigned_to: data.assigned_to || null,
          priority: data.priority,
          notes: data.notes || null,
          checklist: data.checklist,
          status: 'scheduled',
          created_by: data.created_by,
        });

      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      toast({
        title: 'Tarea creada',
        description: 'La tarea de mantenimiento se ha programado correctamente',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message || 'No se pudo crear la tarea',
        variant: 'destructive',
      });
    },
  });
}

/**
 * Hook para actualizar una tarea de mantenimiento
 */
export function useUpdateMaintenance() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<PlotterMaintenanceFormData> }) => {
      const { error } = await api
        .from('plotter_maintenance')
        .update({
          ...data,
          assigned_to: data.assigned_to || null,
        })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      toast({
        title: 'Tarea actualizada',
        description: 'Los cambios se han guardado correctamente',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message || 'No se pudo actualizar la tarea',
        variant: 'destructive',
      });
    },
  });
}

/**
 * Hook para completar una tarea de mantenimiento
 */
export function useCompleteMaintenance() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      id,
      data,
      performedBy
    }: {
      id: string;
      data: CompleteMaintenanceFormData;
      performedBy?: string;
    }) => {
      const totalCost = (data.labor_cost || 0) + (data.parts_cost || 0);

      const { error } = await api
        .from('plotter_maintenance')
        .update({
          status: 'completed',
          completed_date: new Date().toISOString(),
          actual_duration_minutes: data.actual_duration_minutes,
          operating_hours_at_service: data.operating_hours_at_service || null,
          parts_replaced: data.parts_replaced || [],
          consumables_used: data.consumables_used || [],
          labor_cost: data.labor_cost || null,
          parts_cost: data.parts_cost || null,
          total_cost: totalCost > 0 ? totalCost : null,
          notes: data.notes || null,
          checklist: data.checklist,
          performed_by: performedBy || null,
        })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      toast({
        title: 'Tarea completada',
        description: 'El mantenimiento se ha registrado correctamente',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message || 'No se pudo completar la tarea',
        variant: 'destructive',
      });
    },
  });
}

/**
 * Hook para cambiar el estado de una tarea
 */
export function useChangeMaintenanceStatus() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: PlotterMaintenanceStatus }) => {
      const updateData: any = { status };

      if (status === 'in_progress') {
        // Podrías agregar started_at si lo necesitas
      }

      const { error } = await api
        .from('plotter_maintenance')
        .update(updateData)
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message || 'No se pudo cambiar el estado',
        variant: 'destructive',
      });
    },
  });
}

/**
 * Hook para eliminar (soft delete) una tarea
 */
export function useDeleteMaintenance() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await api
        .from('plotter_maintenance')
        .update({ is_active: false })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      toast({
        title: 'Tarea eliminada',
        description: 'La tarea se ha eliminado correctamente',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message || 'No se pudo eliminar la tarea',
        variant: 'destructive',
      });
    },
  });
}

/**
 * Hook para obtener estadísticas de mantenimiento
 */
export function useMaintenanceStats(machineId?: string | null) {
  const { data: tasks = [] } = usePlotterMaintenanceList({ machineId });

  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);

  const stats = {
    total: tasks.length,
    scheduled: tasks.filter(t => t.status === 'scheduled').length,
    inProgress: tasks.filter(t => t.status === 'in_progress').length,
    completed: tasks.filter(t => t.status === 'completed').length,
    overdue: tasks.filter(t => t.status === 'overdue').length,
    completedThisMonth: tasks.filter(t =>
      t.status === 'completed' &&
      t.completed_date &&
      new Date(t.completed_date) >= startOfMonth &&
      new Date(t.completed_date) <= endOfMonth
    ).length,
    nextTask: tasks
      .filter(t => t.status === 'scheduled' && new Date(t.scheduled_date) >= now)
      .sort((a, b) => new Date(a.scheduled_date).getTime() - new Date(b.scheduled_date).getTime())[0] || null,
  };

  return stats;
}
